import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n, { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from "./index";

interface LocaleCtx {
  locale: Locale;
  /** Returns the equivalent path in the other locale. */
  toLocalePath: (path: string, locale?: Locale) => string;
  /** Switch language while preserving the current page. */
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleCtx | undefined>(undefined);

const STORAGE_KEY = "se-locale";

function detectFromPath(pathname: string): Locale {
  const seg = pathname.split("/").filter(Boolean)[0];
  return seg === "en" ? "en" : "ro";
}

/** Strip /en prefix to get the canonical (RO) path. */
function stripPrefix(pathname: string): string {
  if (pathname === "/en") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3);
  return pathname;
}

function withPrefix(path: string, locale: Locale): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (locale === "en") {
    if (clean === "/") return "/en";
    return `/en${clean}`;
  }
  return clean;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = detectFromPath(location.pathname);

  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    document.documentElement.lang = locale;
  }, [locale]);

  // First-visit auto-suggest: if no manual preference saved and browser != ro,
  // redirect "/" to "/en". Manual switches persist via STORAGE_KEY.
  useEffect(() => {
    if (location.pathname !== "/" || typeof window === "undefined") return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return;
    const browser = (navigator.language || "").toLowerCase();
    if (!browser.startsWith("ro")) {
      navigate("/en", { replace: true });
    }
  }, [location.pathname, navigate]);

  const value = useMemo<LocaleCtx>(
    () => ({
      locale,
      toLocalePath: (path, target) => withPrefix(stripPrefix(path), target ?? locale),
      setLocale: (target) => {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(STORAGE_KEY, target);
        }
        const canonical = stripPrefix(location.pathname);
        navigate(withPrefix(canonical, target) + location.search + location.hash);
      },
    }),
    [locale, location.pathname, location.search, location.hash, navigate]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleCtx {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    // Fallback so components mounted outside provider (e.g. tests) don't crash.
    return {
      locale: DEFAULT_LOCALE,
      toLocalePath: (p) => p,
      setLocale: () => {},
    };
  }
  return ctx;
}

/** Inline two-value helper. Avoids ceremonial JSON for one-off strings. */
export function tx<T = string>(ro: T, en: T): T {
  return (i18n.language === "en" ? en : ro) as T;
}

export { SUPPORTED_LOCALES };
